/**
 * Navigation Store Types
 *
 * Type definitions for the navigation history management system.
 * Supports breadcrumb navigation, cross-project navigation, and content slots.
 */

// ============================================================================
// Navigation History Types
// ============================================================================

/**
 * A single entry in the navigation history stack.
 * Each page visit creates one entry.
 */
export interface NavigationEntry {
  /** Unique identifier for this history entry */
  id: string;
  /** Project ID being viewed */
  projectId: string;
  /** Project name for breadcrumb display */
  projectName: string;
  /** Page ID within the project */
  pageId: string;
  /** Page name for breadcrumb display */
  pageName: string;
  /** Unix timestamp when navigation occurred */
  timestamp: number;
  /** Optional parameters passed during navigation */
  parameters?: Record<string, unknown>;
  /** Scroll position to restore on back navigation */
  scrollPosition?: { x: number; y: number };
}

/**
 * Error types for navigation failures.
 * Used to provide meaningful feedback to users.
 */
export type NavigationError =
  | { type: "access_denied"; projectId: string; reason: string }
  | { type: "not_found"; projectId: string }
  | { type: "network_error"; message: string }
  | { type: "storage_full"; message: string };

// ============================================================================
// Cross-Project Navigation Types
// ============================================================================

/**
 * Target for cross-project navigation.
 * Used when a button navigates to a different project.
 */
export interface ProjectNavigationTarget {
  /** Target project ID */
  projectId: string;
  /** Optional specific page within target project */
  pageId?: string;
  /** Organization ID if target is an org-owned project */
  organizationId?: string;
  /** Whether to open in a new browser tab */
  openInNewTab?: boolean;
}

/**
 * Page info for cross-project navigation.
 */
export interface ProjectPageInfo {
  /** Page ID */
  id: string;
  /** Page name */
  name: string;
}

/**
 * Cached project info for the accessible projects list.
 */
export interface AccessibleProjectInfo {
  /** Project name */
  name: string;
  /** Organization ID if org-owned */
  organizationId?: string;
  /** Organization name if org-owned */
  organizationName?: string;
  /** Available pages in this project */
  pages?: ProjectPageInfo[];
}

/**
 * Result of a cross-project navigation attempt
 */
export interface NavigationResult {
  /** Whether the navigation was successful */
  success: boolean;
  /** Error information if navigation failed */
  error?: NavigationError;
}

// ============================================================================
// Content Slot Types (for Phase 4)
// ============================================================================

/**
 * A content slot within a FlexContainer.
 * Allows swapping between multiple content options.
 */
export interface ContentSlot {
  /** The FlexContainer ID that owns this slot */
  containerId: string;
  /** Currently visible content component ID */
  activeContentId: string;
  /** All available content component IDs for this slot */
  availableContentIds: string[];
}

// ============================================================================
// Store State & Actions
// ============================================================================

/**
 * Complete navigation state shape.
 */
export interface NavigationState {
  // History tracking
  /** Stack of navigation entries */
  history: NavigationEntry[];
  /** Current position in history (for back/forward) */
  currentIndex: number;
  /** Maximum entries to keep (prevents unbounded growth) */
  maxHistoryLength: number;

  // Cross-project cache
  /** Cached list of projects user can access */
  accessibleProjects: Map<string, AccessibleProjectInfo>;
  /** Timestamp of last projects fetch (for cache invalidation) */
  lastProjectsFetch: number;

  // Content slots
  /** Map of container ID to content slot state */
  contentSlots: Map<string, ContentSlot>;

  // Error state
  /** Last navigation error, if any */
  lastError: NavigationError | null;
}

/**
 * Navigation store actions.
 */
export interface NavigationActions {
  // ---- History Management ----

  /**
   * Push a new navigation entry onto the history stack.
   * Clears any forward history (entries after currentIndex).
   */
  pushNavigation: (entry: Omit<NavigationEntry, "id" | "timestamp">) => void;

  /**
   * Navigate back in history.
   * @returns The previous entry, or null if at start
   */
  navigateBack: () => NavigationEntry | null;

  /**
   * Navigate forward in history.
   * @returns The next entry, or null if at end
   */
  navigateForward: () => NavigationEntry | null;

  /**
   * Jump to a specific index in history.
   * @returns The entry at that index, or null if invalid
   */
  navigateToHistoryIndex: (index: number) => NavigationEntry | null;

  /**
   * Clear all history (e.g., when switching projects).
   */
  clearHistory: () => void;

  /**
   * Update scroll position for current history entry.
   */
  updateScrollPosition: (position: { x: number; y: number }) => void;

  /**
   * Get breadcrumb entries (history up to current index).
   * @param projectId - Optional project ID to filter by
   */
  getBreadcrumbs: (projectId?: string) => NavigationEntry[];

  // ---- Cross-Project Navigation ----

  /**
   * Set the cached list of accessible projects.
   */
  setAccessibleProjects: (projects: Map<string, AccessibleProjectInfo>) => void;

  /**
   * Check if user can access a specific project.
   * Makes API call if not in cache.
   */
  checkProjectAccess: (projectId: string) => Promise<boolean>;

  /**
   * Fetch all accessible projects for the current user.
   * Uses cache with 5-minute TTL.
   */
  fetchAccessibleProjects: () => Promise<Map<string, AccessibleProjectInfo>>;

  /**
   * Navigate to a different project.
   * Handles permission check and navigation.
   */
  navigateToProject: (target: ProjectNavigationTarget) => Promise<NavigationResult>;

  // ---- Content Slots ----

  /**
   * Register a new content slot for a container.
   * First content ID becomes the active content.
   */
  registerContentSlot: (containerId: string, contentIds: string[]) => void;

  /**
   * Set which content is active in a slot.
   */
  setActiveContent: (containerId: string, contentId: string) => void;

  /**
   * Get the active content ID for a container.
   */
  getActiveContent: (containerId: string) => string | null;

  /**
   * Remove a content slot (on container unmount).
   */
  unregisterContentSlot: (containerId: string) => void;

  // ---- Error Handling ----

  /**
   * Set the last navigation error.
   */
  setError: (error: NavigationError | null) => void;

  /**
   * Clear the last navigation error.
   */
  clearError: () => void;

  // ---- Persistence ----

  /**
   * Persist current state to sessionStorage.
   */
  persistState: () => void;

  /**
   * Restore state from sessionStorage.
   */
  restoreState: () => void;

  // ---- Reset ----

  /**
   * Reset store to initial state.
   */
  reset: () => void;
}

/**
 * Combined store type.
 */
export type NavigationStore = NavigationState & NavigationActions;

// ============================================================================
// Constants
// ============================================================================

/** Default maximum history length */
export const DEFAULT_MAX_HISTORY_LENGTH = 50;

/** SessionStorage key for persisted state */
export const STORAGE_KEY = "terramatrix:navigation:history";

/** Maximum storage size before LRU eviction (1MB) */
export const MAX_STORAGE_SIZE = 1024 * 1024;

/** Cache TTL for accessible projects (5 minutes) */
export const PROJECTS_CACHE_TTL = 5 * 60 * 1000;
